import { useCallback, useMemo } from 'react';
import type { TrackFunction } from './analyticsTypes';

/**
 * React hook for tracking analytics events from components.
 *
 * Provides a `track` function that sends events to the analytics service
 * via IPC. Handles missing electronAPI gracefully (no-op in tests/SSR/web).
 *
 * @example
 * ```tsx
 * const { track } = useAnalytics();
 *
 * // Track a simple event
 * track('feature:query_execute');
 *
 * // Track with additional data
 * track('feature:table_explorer', { engine: 'pgsql' });
 * ```
 */
export function useAnalytics(): { track: TrackFunction } {
  const track = useCallback<TrackFunction>((eventName, data) => {
    // Gracefully handle missing electronAPI (tests, SSR, web demo)
    if (typeof window === 'undefined') return;
    if (!window.electronAPI?.analytics?.track) return;

    // Fire and forget - don't block on analytics
    window.electronAPI.analytics.track(eventName, data).catch(() => {
      // Silently ignore analytics errors - they shouldn't affect UX
    });
  }, []);

  return useMemo(() => ({ track }), [track]);
}
